import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TapSoundService {
  private readonly audioPool: HTMLAudioElement[] = [];
  private readonly poolSize: number = 8; // Pre-create 8 audio elements for rapid playback
  private currentIndex: number = 0;
  private isEnabled: boolean = true;
  private readonly volume: number = 0.05; // Fixed very low volume (5% relative to system volume) - fallback only
  private readonly soundPath: string = 'assets/sound/mixkit-hard-single-key-press-in-a-laptop-2542.wav';
  
  // Web Audio API components for volume independence
  private audioContext: AudioContext | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private gainNode: GainNode | null = null;
  private useWebAudio: boolean = false;
  private readonly fixedGain: number = 0.04; // Fixed very low gain (4% - independent of system volume, always low)
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    // Constructor is now synchronous - initialization happens lazily on first play()
  }

  /**
   * Initialize the audio pool and preload the tap sound file
   * Tries Web Audio API first for volume independence, falls back to HTMLAudioElement
   * Uses lazy initialization - only initializes once, even if called multiple times
   */
  private async initializeAudio(): Promise<void> {
    // If initialization is already in progress or completed, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Create and store the initialization promise
    this.initializationPromise = this.doInitialize();

    try {
      await this.initializationPromise;
    } catch (error) {
      // Reset promise on failure so it can be retried
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * Performs the actual audio initialization
   */
  private async doInitialize(): Promise<void> {
    // Try Web Audio API first for volume independence
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        this.gainNode = this.audioContext.createGain();
        // Set gain to very low value - independent of system volume
        this.gainNode.gain.setValueAtTime(this.fixedGain, this.audioContext.currentTime);
        this.gainNode.connect(this.audioContext.destination);

        const response = await fetch(this.soundPath);
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
          this.useWebAudio = true;
          return; // Successfully initialized Web Audio API
        }
      }
    } catch (error) {
      // Fall back to HTMLAudioElement
      console.warn('Web Audio API initialization failed, using HTMLAudioElement:', error);
    }

    // Fallback to original HTMLAudioElement approach
    try {
      for (let i = 0; i < this.poolSize; i++) {
        const audio = new Audio();
        audio.src = this.soundPath;
        audio.volume = this.volume;
        audio.preload = 'auto';
        
        audio.addEventListener('error', () => {
          // Silently handle errors
        });
        
        this.audioPool.push(audio);
        audio.load();
      }
    } catch (error) {
      console.warn('Failed to initialize audio:', error);
      this.isEnabled = false;
    }
  }

  /**
   * Play the tap sound immediately
   * Uses Web Audio API if available (volume independent), otherwise HTMLAudioElement
   * Triggers lazy initialization if not already initialized
   */
  play(): void {
    if (!this.isEnabled) {
      return;
    }

    // Trigger lazy initialization if not already started
    if (!this.initializationPromise) {
      this.initializeAudio().catch(() => {
        // Initialization failed, will fall back to HTMLAudioElement or fail silently
      });
    }

    // Use Web Audio API if available (volume independent of system volume)
    if (this.useWebAudio && this.audioContext && this.audioBuffer && this.gainNode) {
      try {
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().catch(() => {});
        }

        // Always ensure gain is set to fixed low value before playing
        this.gainNode.gain.setValueAtTime(this.fixedGain, this.audioContext.currentTime);

        const source = this.audioContext.createBufferSource();
        source.buffer = this.audioBuffer;
        source.connect(this.gainNode);
        source.start(0);
        
        source.addEventListener('ended', () => {
          try {
            source.disconnect();
          } catch (e) {}
        });
        return;
      } catch (error) {
        // Fall through to HTMLAudioElement fallback
      }
    }

    // Fallback to HTMLAudioElement (original method)
    if (this.audioPool.length === 0) {
      return;
    }

    try {
      const audio = this.audioPool[this.currentIndex];
      this.currentIndex = (this.currentIndex + 1) % this.audioPool.length;
      
      if (!audio.paused && !audio.ended) {
        audio.pause();
      }
      
      audio.currentTime = 0;
      audio.volume = this.volume;
      
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Public method to initialize audio early (optional)
   * Can be called during app initialization for better performance
   * If not called, initialization happens lazily on first play()
   */
  async initialize(): Promise<void> {
    await this.initializeAudio();
  }

  /**
   * Enable or disable tap sounds
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Check if tap sounds are enabled
   */
  getEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Get the current volume (fixed at low level)
   * Note: Volume is always low regardless of system volume when using Web Audio API
   */
  getVolume(): number {
    return this.useWebAudio ? this.fixedGain : this.volume;
  }
}
